import { Head, Link } from "@inertiajs/react";
import { ArrowRight, CheckCircle2, ImageUp, RefreshCcw } from "lucide-react";

import { ActivationGuide } from "@/components/activation-guide";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STEPS: { title: string; description: string }[] = [
  {
    title: "Open the Publish menu",
    description:
      "In Carrd, click Publish in the top-right corner of the editor when you are ready to update your page.",
  },
  {
    title: "Go to the Media tab",
    description: "Inside the publish flow, switch to the Media tab to see the Share Image controls.",
  },
  {
    title: "Upload your new social image",
    description: "Click Change, choose the OG Pilot image you want to use, and confirm the selection.",
  },
  {
    title: "Crop and resize if needed",
    description: "Use Carrd's crop tool to make sure the preview is framed well, then click Accept to save.",
  },
  {
    title: "Publish the changes",
    description: "Click Publish Changes to push the new share image live to your Carrd site.",
  },
];

export default function CarrdSocialImage({
  page_title: pageTitle = "Carrd Social Image — Custom Open Graph Preview",
}: {
  page_title?: string;
}) {
  return (
    <>
      <Head title={pageTitle} />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 pb-20 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <div className="my-16 text-center">
            <Badge className="mb-3 bg-orange-100 tracking-[0.2em] text-orange-700 uppercase dark:bg-orange-900/30 dark:text-orange-300">
              Carrd guide
            </Badge>
            <h1 className="mb-4 text-4xl font-bold text-balance text-gray-900 md:text-5xl dark:text-white">
              Custom Carrd social images for link previews
            </h1>
            <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
              Replace Carrd&apos;s default screenshot with a branded share image. Generate it in OG Pilot, upload it
              under Publish → Media → Share Image, or create the same card from the API and MCP.
            </p>
            <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-gray-600 dark:text-gray-300">
              Comparing the default screenshot, a manual Share Image, a one-off generate, the API, and MCP?{" "}
              <Link href="/carrd-og-image" className="font-medium text-orange-700 hover:underline dark:text-orange-300">
                Read the Carrd OG image comparison
              </Link>
              .
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
              <Button asChild size="lg" className="bg-orange-500 text-white hover:bg-orange-600">
                <Link href="/new">
                  Generate a Carrd image
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link href="/docs">Use the API</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link href="/docs/mcp">Use MCP</Link>
              </Button>
            </div>
          </div>

          <ActivationGuide
            title="Upload the image, then prove the card changed"
            links={[
              {
                href: "/new",
                label: "Generate a Carrd image",
                detail: "Start from the Carrd URL. Download the card you will upload as the share image.",
              },
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "Confirm og:image is your file, not Carrd's screenshot, before you share the link.",
              },
              {
                href: "/docs",
                label: "Use the API",
                detail: "Create the next image from code when the page changes and you do not want to export by hand.",
              },
              {
                href: "/docs/mcp",
                label: "Use MCP",
                detail: "Have an agent generate the same Carrd card from the public URL.",
              },
            ]}
          >
            <p>
              Carrd serves a screenshot until you set a share image under Publish → Media. That screenshot is a poor
              unfurl: the type is small and the layout is whatever happened to be on screen. Generate a 1200×630 image,
              upload it, publish, and check the URL. Facebook and LinkedIn keep the old card until you scrape again.
              WordPress sites should use the{" "}
              <Link href="/wordpress" className="font-medium text-orange-700 hover:underline dark:text-orange-300">
                WordPress open graph plugin
              </Link>{" "}
              instead of this upload.
            </p>
          </ActivationGuide>

          <section className="mb-20 grid gap-6 md:grid-cols-5">
            <div className="col-span-5 rounded-3xl bg-white/80 p-8 shadow-xl backdrop-blur md:col-span-3 dark:bg-gray-900/80 dark:shadow-none">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-2 text-sm font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                <ImageUp className="h-4 w-4" />
                <span>What you need</span>
              </div>
              <h2 className="mb-3 text-2xl font-semibold text-gray-900 dark:text-white">Before you start</h2>
              <ul className="space-y-3 text-sm leading-6 text-gray-600 dark:text-gray-400">
                <li>
                  An OG Pilot image for the page. Generate a one-off from a URL, or download one from a connected
                  domain.
                </li>
                <li>A Carrd site with a Pro Lite plan or higher (share images require it).</li>
                <li>Access to the Carrd editor for the site you want to update.</li>
              </ul>
            </div>
            <div className="col-span-5 rounded-3xl border border-orange-200 bg-orange-50/80 p-8 shadow-inner md:col-span-2 dark:border-orange-900/40 dark:bg-orange-900/10">
              <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Time to complete</h3>
              <div className="flex items-center gap-3 text-orange-700 dark:text-orange-200">
                <RefreshCcw className="h-5 w-5" />
                <span className="text-base font-medium">About 2 minutes</span>
              </div>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                Carrd may take up to 24 hours to refresh the preview if the page was already shared. We cover cache
                reset tips below.
              </p>
            </div>
          </section>

          <section className="mx-auto mb-16 max-w-2xl">
            <div className="mb-8 text-center">
              <h2 className="mb-3 text-3xl font-bold text-gray-900 dark:text-white">Follow these Carrd steps</h2>
              <p className="mx-auto text-gray-600 dark:text-gray-300">
                These steps mirror Carrd&apos;s official instructions with a little extra guidance so you can plug in
                the image you generated with OG Pilot.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-1">
              {STEPS.map((step, index) => (
                <div
                  key={step.title}
                  className="flex gap-4 rounded-2xl bg-white p-6 shadow-lg transition-transform hover:-translate-y-1 dark:bg-gray-900/90 dark:shadow-none"
                >
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-2xl font-bold text-white">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">{step.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 dark:border-emerald-900/40 dark:bg-emerald-900/20">
              <CheckCircle2 className="mt-1 h-6 w-6 text-emerald-600 dark:text-emerald-300" />
              <p className="text-sm text-gray-700 dark:text-gray-300">
                You are done! The selected share image will appear on social platforms the next time your link is
                shared.
              </p>
            </div>
          </section>

          <section className="mb-16 rounded-3xl bg-white p-8 shadow-xl dark:bg-gray-900/95 dark:shadow-none">
            <div className="grid gap-8 md:grid-cols-2 md:items-start">
              <div>
                <h2 className="mb-3 text-2xl font-semibold text-gray-900 dark:text-white">
                  Speed up social preview refreshes
                </h2>
                <p className="text-sm leading-6 text-gray-600 dark:text-gray-400">
                  Social networks cache previews aggressively. After publishing, use these tools to make sure your new
                  image shows up everywhere:
                </p>
                <ul className="mt-5 space-y-3 text-sm text-orange-600 dark:text-orange-300">
                  <li>
                    <Link href="/open-graph-debugger" className="hover:underline">
                      OG Pilot Open Graph debugger
                    </Link>
                  </li>
                  <li>
                    <a
                      href="https://developers.facebook.com/tools/debug/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      Facebook Sharing Debugger
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://cards-dev.twitter.com/validator"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      X (Twitter) Card Validator
                    </a>
                  </li>
                </ul>
              </div>
              <div className="rounded-2xl border border-orange-200 bg-orange-100/60 p-6 text-sm text-gray-700 dark:border-orange-900/40 dark:bg-orange-900/10 dark:text-gray-300">
                <p className="mb-3 font-semibold text-gray-900 dark:text-white">Why does this matter?</p>
                <p>
                  If your Carrd site was shared before the update, platforms often show the old screenshot until their
                  cache expires. Triggering a re-scrape forces them to fetch your new OG Pilot design immediately.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-20 overflow-hidden rounded-3xl bg-gradient-to-r from-orange-500 via-orange-600 to-rose-500 p-10 text-white shadow-xl">
            <div className="grid gap-6 md:grid-cols-2 md:items-center">
              <div>
                <h2 className="mb-4 text-3xl font-bold">Need more Carrd-ready social images?</h2>
                <p className="mb-6 text-sm leading-6 text-orange-50">
                  Generate a one-off image, or create the same card from the API and MCP whenever the page changes.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <Button asChild size="lg" className="bg-white text-orange-600 hover:bg-orange-50">
                    <Link href="/new">
                      Generate a Carrd image
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-white/70 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  >
                    <Link href="/docs">Use the API</Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-white/70 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  >
                    <Link href="/docs/mcp">Use MCP</Link>
                  </Button>
                </div>
              </div>
              <div className="rounded-2xl border border-white/30 bg-white/10 p-6 text-sm leading-6">
                <p className="mb-3 font-semibold tracking-wider text-orange-100 uppercase">Pro tip</p>
                <p>
                  Host the downloaded OG Pilot image on your own domain or CDN if you prefer full control. Just upload
                  the hosted URL to Carrd using the same steps above.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
      <SiteFooter />
    </>
  );
}
